import {
  assertUploadWithin10Mb,
  uploadSizeLimitApiMessage,
} from "@/constants/uploadLimits";
import { getErrorMessage } from "@/utils/errorMessage";
import type {
  VisitAttachment,
  VisitAttachmentListResponse,
  VisitAttachmentStats,
  VisitAttachmentDownloadUrl,
} from "../pages/MonitoringTracker/types/monitoringVisit.types";
import type {
  IVisitAttachmentRepository,
  VisitAttachmentListParams,
} from "../repositories/VisitAttachmentRepository";

export interface IVisitAttachmentUseCase {
  upload(visitId: string, file: File): Promise<VisitAttachment>;
  list(
    params?: VisitAttachmentListParams,
  ): Promise<VisitAttachmentListResponse>;
  getByVisit(visitId: string): Promise<VisitAttachment[]>;
  getStats(visitId: string): Promise<VisitAttachmentStats>;
  getById(id: string): Promise<VisitAttachment>;
  delete(id: string): Promise<void>;
  getDownloadUrl(
    id: string,
    expiresIn?: number,
  ): Promise<VisitAttachmentDownloadUrl>;
}

export class VisitAttachmentUseCase implements IVisitAttachmentUseCase {
  constructor(private readonly repository: IVisitAttachmentRepository) {}

  async upload(visitId: string, file: File): Promise<VisitAttachment> {
    if (!visitId?.trim()) {
      throw new Error("Visit ID is required");
    }
    assertUploadWithin10Mb(file);
    try {
      return await this.repository.upload(visitId, file);
    } catch (error) {
      const sizeMessage = uploadSizeLimitApiMessage(error);
      throw new Error(
        sizeMessage ?? getErrorMessage(error, "Failed to upload attachment"),
      );
    }
  }

  async list(
    params?: VisitAttachmentListParams,
  ): Promise<VisitAttachmentListResponse> {
    return this.repository.list(params);
  }

  async getByVisit(visitId: string): Promise<VisitAttachment[]> {
    return this.repository.getByVisit(visitId);
  }

  async getStats(visitId: string): Promise<VisitAttachmentStats> {
    return this.repository.getStats(visitId);
  }

  async getById(id: string): Promise<VisitAttachment> {
    return this.repository.getById(id);
  }

  async delete(id: string): Promise<void> {
    return this.repository.delete(id);
  }

  async getDownloadUrl(
    id: string,
    expiresIn = 3600,
  ): Promise<VisitAttachmentDownloadUrl> {
    return this.repository.getDownloadUrl(id, expiresIn);
  }
}
